import * as amplitude from "@amplitude/analytics-browser";
import { isRedoEmailAddress } from "@redotech/account-common/redo-domains";
import { ReturnsFlagKey } from "@redotech/feature-flags-keys-returns/keys";
import { useHandler } from "@redotech/react-util/hook";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { useContext, useSyncExternalStore } from "react";
import { useTeamFeatureFlag } from "../use-team-feature-flag";
import { UserContext } from "../user";
import { resolveReturnsSettingsExperience } from "./returns-settings-experience";

/** Mirrors the merchant app's PlatformEvents entry; kept here so this hook
 * does not pull the app's event catalogue into the common package. */
const EXPERIENCE_SWITCHED_EVENT = "settings/returns-experience-switched";

const LEGACY_STORAGE_KEY = "redo.returns-settings-legacy";
const LEGACY_PREFERENCE_CHANGED = "redo:returns-settings-legacy-changed";

/** Falls back to this when storage is unavailable, so a switch still applies to the tab. */
let inMemoryLegacyPreference = false;

function readLegacyPreference(): boolean {
  try {
    return localStorage.getItem(LEGACY_STORAGE_KEY) === "true";
  } catch {
    return inMemoryLegacyPreference;
  }
}

function writeLegacyPreference(prefersLegacy: boolean) {
  inMemoryLegacyPreference = prefersLegacy;
  try {
    if (prefersLegacy) {
      localStorage.setItem(LEGACY_STORAGE_KEY, "true");
    } else {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    }
  } catch {
    // Private mode or quota exhausted; the in-memory value still applies.
  }
  window.dispatchEvent(new Event(LEGACY_PREFERENCE_CHANGED));
}

function subscribeToLegacyPreference(onChange: () => void) {
  window.addEventListener(LEGACY_PREFERENCE_CHANGED, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(LEGACY_PREFERENCE_CHANGED, onChange);
    window.removeEventListener("storage", onChange);
  };
}

export interface ReturnsSettingsExperience {
  /** Whether the user may switch between the new page and the old pages. */
  canSwitch: boolean;
  pending: boolean;
  useNewUi: boolean;
  switchToLegacy(): void;
  switchToNew(): void;
}

export function useReturnsSettingsExperience(): ReturnsSettingsExperience {
  const user = useContext(UserContext);
  const team = useContext(TeamContext);
  const isRedoUser = isRedoEmailAddress(user?.email);
  const rollout = useTeamFeatureFlag(
    ReturnsFlagKey.CONSOLIDATED_RETURNS_SETTINGS,
    { forceEnabledForRedoUsers: false },
  );
  const requiredForRedoUsers = useTeamFeatureFlag(
    ReturnsFlagKey.CONSOLIDATED_RETURNS_SETTINGS_REQUIRED_FOR_REDO_USERS,
    { forceEnabledForRedoUsers: false },
  );
  const prefersLegacy = useSyncExternalStore(
    subscribeToLegacyPreference,
    readLegacyPreference,
  );

  const switchToLegacy = useHandler(() => {
    writeLegacyPreference(true);
    amplitude.track(EXPERIENCE_SWITCHED_EVENT, { to: "legacy" });
  });

  const switchToNew = useHandler(() => {
    writeLegacyPreference(false);
    amplitude.track(EXPERIENCE_SWITCHED_EVENT, { to: "new" });
  });

  const { useNewUi, canSwitch } = resolveReturnsSettingsExperience({
    isRedoUser,
    teamInRollout: rollout.enabled,
    enabledForTeam: !!team?.settings.returns?.consolidatedSettingsEnabled,
    requiredForRedoUsers: requiredForRedoUsers.enabled,
    prefersLegacy,
  });

  return {
    canSwitch,
    pending: rollout.pending || (isRedoUser && requiredForRedoUsers.pending),
    useNewUi,
    switchToLegacy,
    switchToNew,
  };
}
